import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Divider,
} from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const NAVY = '#052F5D';

/**
 * Finalize locks the record. We show a checklist of what's filled vs
 * what's missing, gate the action on the required items, and call out
 * the consequence ("cannot be edited") plainly.
 *
 * `amending` switches the copy for closing an open amendment. The checklist
 * still gates: an amendment can just as easily remove the last diagnosis as
 * add one, and a reissued report must not go out incomplete.
 */
export default function FinalizeDialog({ open, encounter, onClose, onConfirm, submitting, amending = false }) {
    const items = buildChecklist(encounter);
    const missingRequired = items.filter((i) => i.required && !i.satisfied);
    const canFinalize = missingRequired.length === 0;

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: NAVY, fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <LockOutlinedIcon fontSize="small" />
                {amending ? 'Complete this amendment?' : 'Finalize this encounter?'}
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {amending
                        ? `The report is reissued with your corrections and locks again. Your reason and the
                           sections you changed are kept in this encounter's internal history and the audit
                           log — the PDF itself carries no mention of the amendment.`
                        : `Once finalized, the encounter becomes part of the patient's permanent record and can no
                           longer be edited. You'll still be able to generate and email the summary PDF.`}
                </Typography>

                <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700, fontSize: 10 }}
                >
                    Documentation checklist
                </Typography>

                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {items.map((it) => (
                        <ListItem key={it.label} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {it.satisfied ? (
                                    <CheckCircleOutlineRoundedIcon
                                        sx={{ fontSize: 18, color: 'success.main' }}
                                    />
                                ) : (
                                    <RadioButtonUncheckedRoundedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: it.required ? 'error.main' : 'text.disabled',
                                        }}
                                    />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: it.required ? 600 : 400 }}>
                                            {it.label}
                                        </Typography>
                                        {it.required && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: 'error.main', fontWeight: 600 }}
                                            >
                                                required
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                secondary={it.detail}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    ))}
                </List>

                {!canFinalize && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1.5 }}>
                            Please complete the required items before {amending ? 'reissuing the report' : 'finalizing'}.
                        </Alert>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ color: 'text.secondary' }}>
                    Keep editing
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={!canFinalize || submitting}
                    variant="contained"
                    sx={{
                        bgcolor: NAVY,
                        '&:hover': { bgcolor: '#03244a' },
                        '&.Mui-disabled': { bgcolor: 'rgba(5,47,93,0.25)', color: '#fff' },
                    }}
                >
                    {amending
                        ? (submitting ? 'Reissuing…' : 'Complete amendment')
                        : (submitting ? 'Finalizing…' : 'Finalize encounter')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function buildChecklist(e) {
    const has = (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v && String(v).trim()));
    const vitalsCount = (e?.vitals || []).length;
    const examFilled = (e?.physicalExam || []).filter((r) => r.status === 'normal' || r.status === 'abnormal').length;
    const dx = e?.diagnoses || [];
    return [
        {
            label: 'Chief complaint',
            required: true,
            satisfied: has(e?.chiefComplaint),
            detail: has(e?.chiefComplaint) ? undefined : 'Why the patient came in.',
        },
        {
            label: 'At least one diagnosis',
            required: true,
            satisfied: dx.length > 0,
            detail: dx.length ? `${dx.length} recorded` : 'Add a diagnosis (ICD-10 or free text).',
        },
        {
            label: 'History of presenting illness',
            required: false,
            satisfied: has(e?.historyOfPresenting),
        },
        {
            label: 'Vitals',
            required: false,
            satisfied: vitalsCount > 0,
            detail: vitalsCount ? `${vitalsCount} reading${vitalsCount > 1 ? 's' : ''}` : undefined,
        },
        {
            label: 'Physical examination',
            required: false,
            satisfied: examFilled > 0,
            detail: examFilled ? `${examFilled} system${examFilled > 1 ? 's' : ''} reviewed` : undefined,
        },
        {
            label: 'Treatment plan',
            required: false,
            satisfied: (e?.treatment || []).length > 0
                || Boolean(e?.treatmentNone)
                || has(e?.procedureNotes),
        },
    ];
}

export { buildChecklist };
